import { identity, pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import {
    RestrictionData,
    RestrictionExceptionFilter,
    RestrictionMultiConditionData,
    RestrictionSetData,
    RestrictionSplicingMode,
    UpdateRestrictionData,
} from '@mews-commander/data-access';
import { FeatureFlag, RouteName } from '@mews-commander/enums';
import { AsyncAction, CommanderThunkAction, CommanderThunkDispatch, WithId } from '@mews-commander/types';
import { TimeUnit } from '@mews/enums';
import { createAction, lt, showSuccessAlert } from '@mews/framework';
import { createFetchAction, daysArrayToDaysParameters } from '@mews-commander/utils';
import { getServiceTimeUnit, getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { resetRestrictionState, updateRestrictionsState } from '@mews-commander/legacy-entities/src/ducks/restrictions';
import { getRates } from '@mews-commander/legacy-entities/src/ducks/rates';
import { getRateGroups } from '@mews-commander/legacy-entities/src/ducks/rateGroups';
import { getResourceCategories } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getActiveResourceCategoryTypes } from '@mews-commander/legacy-entities/src/ducks/categories';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import { convertFormValuesToRestrictionData } from '../utils';
import {
    FORM_MODE_SETUP,
    RESTRICTIONS_ADD,
    RESTRICTIONS_CLEAR,
    RESTRICTIONS_SET,
    RESTRICTIONS_UPDATE,
} from '../actionTypes';
import { navigateInModal } from '../../app/actions/common';
import {
    DeleteFormValues,
    EditFormValues,
    MultiConditionsCreateFormValues,
    SingleConditionsCreateFormValues,
} from '../components/form/types';
import { fetchRestrictions } from './api';
import { navigateToRestrictionIndexPage } from './navigation';

export const setupFormMode = ({ currentRestrictionId, formType }: $TSFixMe, scope: $TSFixMe) =>
    createAction(FORM_MODE_SETUP, { currentRestrictionId, formType }, { scope });

export const setRestriction =
    ({
        formValues,
        serviceId,
        preview,
    }: {
        formValues: MultiConditionsCreateFormValues;
        serviceId: string;
        preview?: boolean;
    }): CommanderThunkAction<AsyncAction> =>
    async (dispatch: CommanderThunkDispatch, getState, { repositories, container }) => {
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const timeUnit: TimeUnit = getServiceTimeUnit(getState(), { id: serviceId });

        const dateTimeLocalizationInfo = getCurrentDateTimeLocalizationInfo(getState());

        const { conditions, exceptions } = convertFormValuesToRestrictionData(
            formValues,
            timeUnit,
            container,
            dateTimeLocalizationInfo
        );
        const updatedData = {
            exceptions,
            conditions: {
                ...conditions,
                days: conditions.days && daysArrayToDaysParameters(conditions.days),
            },
        } as RestrictionSetData;

        const promise = repositories.restrictions.set({
            data: [updatedData],
            serviceId,
            preview,
            splicingMode: formValues.splicingMode,
        });
        return dispatch(createFetchAction(RESTRICTIONS_SET, promise));
    };

export const replaceRestriction =
    ({
        formValues,
        serviceId,
        id,
        preview,
    }: {
        formValues: EditFormValues;
        serviceId: string;
        preview?: boolean;
    } & WithId): CommanderThunkAction<AsyncAction> =>
    async (dispatch: CommanderThunkDispatch, getState, { repositories, container }) => {
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const timeUnit: TimeUnit = getServiceTimeUnit(getState(), { id: serviceId });

        const dateTimeLocalizationInfo = getCurrentDateTimeLocalizationInfo(getState());

        const { conditions, exceptions } = convertFormValuesToRestrictionData(
            formValues,
            timeUnit,
            container,
            dateTimeLocalizationInfo
        );
        const rateSpaceConditions = {
            rateGroupIds: formValues.rateGroupId ? [formValues.rateGroupId] : null,
            baseRateIds: formValues.baseRateId ? [formValues.baseRateId] : null,
            exactRateIds: formValues.exactRateId ? [formValues.exactRateId] : null,
            resourceCategoryIds: formValues.resourceCategoryId ? [formValues.resourceCategoryId] : null,
            resourceCategoryTypes: formValues.resourceCategoryType ? [formValues.resourceCategoryType] : null,
        };
        const updatedData = {
            exceptions,
            conditions: {
                ...conditions,
                ...rateSpaceConditions,
                days: conditions.days && daysArrayToDaysParameters(conditions.days),
            },
        } as RestrictionSetData;

        const promise = repositories.restrictions.replace({
            data: [updatedData],
            serviceId,
            replaceRestrictionId: id,
            preview,
            splicingMode: RestrictionSplicingMode.MergeExceptions,
        });
        return dispatch(createFetchAction(RESTRICTIONS_SET, promise));
    };

const createRateSpaceConditionCombinations = (
    {
        clearRelevantRates,
        clearRelevantResourceCategories,
        exactRateIds,
        baseRateIds,
        rateGroupIds,
        resourceCategoryIds,
        resourceCategoryTypes,
    }: DeleteFormValues,
    {
        rates,
        rateGroups,
        resourceCategories,
        resourceCategoryTypeEnums,
    }: {
        rates: ReturnType<typeof getRates>;
        rateGroups: ReturnType<typeof getRateGroups>;
        resourceCategories: ReturnType<typeof getResourceCategories>;
        resourceCategoryTypeEnums: ReturnType<typeof getActiveResourceCategoryTypes>;
    }
) => {
    const isAllRates = !rateGroupIds && !baseRateIds && !exactRateIds;
    const isAllSpaces = !resourceCategoryIds && !resourceCategoryTypes;

    const initRateData = {
        exactRateIds,
        baseRateIds,
        rateGroupIds,
    };

    const initResourceCategoryData = {
        resourceCategoryIds,
        resourceCategoryTypes,
    };

    const extendedExactRateIds = Object.values(rates)
        .filter(
            ({ baseRateId, rateGroupId }) =>
                isAllRates || rateGroupIds?.includes(rateGroupId) || (baseRateId && baseRateIds?.includes(baseRateId))
        )
        .map(({ id }) => id);

    const extendedBaseRateIds = Object.values(rates)
        .filter(({ rateGroupId }) => isAllRates || rateGroupIds?.includes(rateGroupId))
        .map(({ id }) => id);

    const extendedRateGroupIds = Object.values(rateGroups).map(({ id }) => id);

    const extendedResourceCategoryIds = Object.values(resourceCategories)
        .filter(({ type }) => isAllSpaces || resourceCategoryTypes?.includes(type))
        .map(({ id }) => id);

    const extendedResourceCategoryTypes = resourceCategoryTypeEnums.map(({ name }) => name);

    const rateData = pipe(
        [initRateData],
        clearRelevantRates && array.isNonEmpty(extendedExactRateIds)
            ? array.append<typeof initRateData>({
                  exactRateIds: extendedExactRateIds,
                  baseRateIds: null,
                  rateGroupIds: null,
              })
            : identity,
        clearRelevantRates && (isAllRates || rateGroupIds) && array.isNonEmpty(extendedBaseRateIds)
            ? array.append<typeof initRateData>({
                  exactRateIds: null,
                  baseRateIds: extendedBaseRateIds,
                  rateGroupIds: null,
              })
            : identity,
        clearRelevantRates && isAllRates && array.isNonEmpty(extendedRateGroupIds)
            ? array.append<typeof initRateData>({
                  exactRateIds: null,
                  baseRateIds: null,
                  rateGroupIds: extendedRateGroupIds,
              })
            : identity
    );

    const resourceCategoryData = pipe(
        [initResourceCategoryData],
        clearRelevantResourceCategories && array.isNonEmpty(extendedResourceCategoryIds)
            ? array.append<typeof initResourceCategoryData>({
                  resourceCategoryIds: extendedResourceCategoryIds,
                  resourceCategoryTypes: null,
              })
            : identity,
        clearRelevantResourceCategories && isAllSpaces && array.isNonEmpty(extendedResourceCategoryTypes)
            ? array.append<typeof initResourceCategoryData>({
                  resourceCategoryIds: null,
                  resourceCategoryTypes: extendedResourceCategoryTypes,
              })
            : identity
    );

    return rateData.flatMap((rateConditions) =>
        resourceCategoryData.map((resourceCategoryConditions) => ({
            ...rateConditions,
            ...resourceCategoryConditions,
        }))
    );
};

export const clearRestrictions =
    (serviceId: string, formValues: DeleteFormValues, preview: boolean): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories, container }) => {
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const timeUnit: TimeUnit = getServiceTimeUnit(getState(), { id: serviceId });

        const state = getState();
        const dateTimeLocalizationInfo = getCurrentDateTimeLocalizationInfo(state);

        const { conditions } = convertFormValuesToRestrictionData(
            formValues,
            timeUnit,
            container,
            dateTimeLocalizationInfo
        );

        const rates = getRates(state);
        const rateGroups = getRateGroups(state);
        const resourceCategories = getResourceCategories(state, { serviceId });
        const resourceCategoryTypeEnums = getActiveResourceCategoryTypes(state, { serviceId });

        const rateSpaceConditionCombinations = createRateSpaceConditionCombinations(formValues, {
            rateGroups,
            rates,
            resourceCategories,
            resourceCategoryTypeEnums,
        });

        const data = rateSpaceConditionCombinations.map(
            (rateSpaceConditions) =>
                ({
                    ...conditions,
                    ...rateSpaceConditions,
                    days: conditions.days && daysArrayToDaysParameters(conditions.days),
                }) as RestrictionMultiConditionData
        );

        const restrictionExceptionFilter: RestrictionExceptionFilter = {
            anyException:
                formValues.restrictionExceptionFilter === 'allExceptions' ||
                formValues.restrictionExceptionFilter === 'anyException',
            noException:
                formValues.restrictionExceptionFilter === 'allExceptions' ||
                formValues.restrictionExceptionFilter === 'noException',
        };

        const promise = repositories.restrictions.clear({
            serviceId,
            data,
            preview,
            origin: formValues.origin,
            restrictionExceptionFilter,
        });
        return dispatch(createFetchAction(RESTRICTIONS_CLEAR, promise));
    };

export const updateRestriction =
    ({ formValues, serviceId }: { formValues: EditFormValues; serviceId: string }): CommanderThunkAction<AsyncAction> =>
    async (dispatch: CommanderThunkDispatch, getState, { repositories, container }) => {
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const timeUnit: TimeUnit = getServiceTimeUnit(getState(), { id: serviceId });
        const dateTimeLocalizationInfo = getCurrentDateTimeLocalizationInfo(getState());

        const updateRestrictionData = {
            ...convertFormValuesToRestrictionData(formValues, timeUnit, container, dateTimeLocalizationInfo),
            id: formValues.id,
        } as UpdateRestrictionData;

        const promise = repositories.restrictions
            .update({ restrictions: [updateRestrictionData], serviceId })
            .then(({ restrictions }) => {
                if (restrictions) {
                    dispatch(updateRestrictionsState(restrictions, serviceId));
                }
                return restrictions;
            });
        return dispatch(createFetchAction(RESTRICTIONS_UPDATE, promise));
    };

const addRestriction =
    ({
        formValues,
        serviceId,
    }: {
        formValues: SingleConditionsCreateFormValues;
        serviceId: string;
    }): CommanderThunkAction<AsyncAction> =>
    async (dispatch: CommanderThunkDispatch, getState, { repositories, container }) => {
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 2.
        const timeUnit: TimeUnit = getServiceTimeUnit(getState(), { id: serviceId });
        const dateTimeLocalizationInfo = getCurrentDateTimeLocalizationInfo(getState());

        const updateRestrictionData = {
            ...convertFormValuesToRestrictionData(formValues, timeUnit, container, dateTimeLocalizationInfo),
        } as RestrictionData;

        const promise = repositories.restrictions
            .add({ restrictions: [updateRestrictionData], serviceId })
            .then(({ restrictions }) => {
                if (restrictions) {
                    dispatch(updateRestrictionsState(restrictions, serviceId));
                }
                return restrictions;
            });
        return dispatch(createFetchAction(RESTRICTIONS_ADD, promise));
    };

export const addRestrictionAndSwitchToEditMode =
    (values: $TSFixMe, ownerId: $TSFixMe) =>
    async (dispatch: $TSFixMe, getState: $TSFixMe, { resolveRoute, featureFlagService }: $TSFixMe) => {
        const multiselectRestrictionsFormEnabled = featureFlagService.hasFlag(FeatureFlag.restrictionSplicingEnabled);
        const state = getState();
        const timeUnit = getTimeUnitByServiceId(state, { serviceId: ownerId });
        if (multiselectRestrictionsFormEnabled && timeUnit === TimeUnit.Day) {
            await dispatch(setRestriction({ formValues: values, serviceId: ownerId }));
            showSuccessAlert({ description: lt('RestrictionsHaveBeenCreated') });
            dispatch(resetRestrictionState(ownerId));
            dispatch(fetchRestrictions({ serviceId: ownerId }));
            dispatch(navigateToRestrictionIndexPage(ownerId));
        } else {
            const { value: restrictions } = await dispatch(addRestriction({ formValues: values, serviceId: ownerId }));
            const [{ id }] = restrictions;
            showSuccessAlert({ description: lt('RestrictionsHaveBeenCreated') });
            const restrictionDetailUrl = resolveRoute(RouteName.RestrictionDetail, {
                restrictionId: id,
                query: { OwnerId: ownerId },
            });
            dispatch(setupFormMode({ isAddForm: false, currentRestrictionId: id }, ownerId));
            dispatch(navigateInModal(restrictionDetailUrl));
        }
    };
